import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

export const HeroSection = memo(() => {
  const navigate = useNavigate();
  
  return (
    <section className="relative lg:py-32 px-6 lg:px-8 py-[40px] overflow-hidden">
      {/* Safari-compatible wave animation using CSS transforms instead of SVG path animation */}
      <style>{`
        @keyframes wave-float {
          0%, 100% {
            transform: translateY(0) translateX(0) rotate(0deg);
          }
          25% {
            transform: translateY(-15px) translateX(5px) rotate(0.5deg);
          }
          50% {
            transform: translateY(-25px) translateX(-5px) rotate(-0.5deg);
          }
          75% {
            transform: translateY(-10px) translateX(8px) rotate(0.3deg);
          }
        }
        
        @keyframes wave-shimmer {
          0%, 100% {
            opacity: 0.6;
          }
          50% {
            opacity: 0.9;
          }
        }
        
        .wave-container {
          animation: wave-float 8s ease-in-out infinite, wave-shimmer 4s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .wave-container-desktop {
          animation: wave-float 10s ease-in-out infinite, wave-shimmer 5s ease-in-out infinite;
          will-change: transform, opacity;
        }
      `}</style>
      
      {/* Safari-compatible wave background using CSS gradients and transforms */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Mobile wave */}
        <div 
          className="wave-container absolute -left-[20%] top-[5%] w-[140%] h-[60%] lg:hidden"
          style={{
            background: 'linear-gradient(135deg, transparent 0%, hsl(210 79% 58% / 0.3) 30%, hsl(210 100% 65% / 0.5) 50%, hsl(200 85% 60% / 0.3) 70%, transparent 100%)',
            filter: 'blur(60px)',
            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
          }}
        />
        
        {/* Desktop wave */}
        <div 
          className="wave-container-desktop absolute -left-[10%] top-0 w-[120%] h-[80%] hidden lg:block"
          style={{
            background: 'linear-gradient(135deg, transparent 0%, hsl(210 79% 58% / 0.35) 25%, hsl(210 100% 65% / 0.5) 50%, hsl(200 85% 60% / 0.35) 75%, transparent 100%)',
            filter: 'blur(80px)',
            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
          }}
        />
      </div>
      
      <div className="relative max-w-6xl mx-auto text-center space-y-10">
        {/* Badge - elegant serif italic like Pulse Robot */}
        <div className="inline-flex animate-fade-in">
          <span className="text-sm italic text-primary/80 tracking-wide" style={{ fontFamily: "'Playfair Display', 'Georgia', serif" }}>
            Nouveau concept cadeau
          </span>
        </div>

        {/* Title - NO animation on H1 to ensure fast LCP */}
        <div className="space-y-6">
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            Offrez un souvenir
            <br />
            <span className="bg-gradient-to-r from-primary via-primary-light to-primary bg-clip-text text-transparent">
              qui traverse le temps
            </span>
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Transformez vos photos et messages en un <strong className="py-0">QR Code unique</strong> accessible à vie. 
            L'alternative moderne au cadre photo classique. <strong className="mx-0">Sans abonnement</strong>.
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <Button size="xl" variant="premium" onClick={() => navigate('/select-relationship')} className="gap-3 group px-8">
            Créer mon QR Portrait
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button size="xl" variant="outline" onClick={() => navigate('/demo')} className="rounded-full border-2">
            Voir la démo interactive
          </Button>
        </div>

        {/* Social Proof */}
        <div className="flex flex-wrap items-center justify-center gap-8 pt-12 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-foreground">3 500 F</div>
            <div className="text-sm text-muted-foreground">Pour toute la vie</div>
          </div>
          <div className="w-px h-12 bg-border hidden sm:block" />
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-foreground">2 min</div>
            <div className="text-sm text-muted-foreground">Temps de création</div>
          </div>
          <div className="w-px h-12 bg-border hidden sm:block" />
          <div className="text-center">
            <div className="text-3xl lg:text-4xl font-bold text-foreground">∞</div>
            <div className="text-sm text-muted-foreground">Accès illimité</div>
          </div>
        </div>
      </div>
    </section>
  );
});

HeroSection.displayName = 'HeroSection';
